import { useCallback, useMemo } from 'react';
import { toISODateString } from '@chainflip/utils/date';
import { UTCDate } from '@date-fns/utc';
import { BigNumber } from 'bignumber.js';
import { type ChartAggregationIntervalOption } from '@/shared/hooks/useChartAggregation';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { getTruncatedChartDates, getTruncatedDataPointDate } from '@/shared/utils/date';
import { triggerCsvDownload } from '@/shared/utils/export';
import TokenAmount from '@/shared/utils/TokenAmount';
import { getDelegatorBalanceAndRewardsAggregateByDateRange } from '../graphql/reporting/delegatorActivity';

export const useDelegationSnapshotData = (
  idSs58: string,
  startDate: UTCDate,
  endDate: UTCDate,
  aggregationInterval: ChartAggregationIntervalOption['value'],
) => {
  const { data, isLoading } = useGqlQuery(getDelegatorBalanceAndRewardsAggregateByDateRange, {
    refetchInterval: 60_000,
    context: { clientName: 'reportingService' },
    variables: {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      idSs58,
    },
  });

  const minDate = startDate;
  const chartDates = useMemo(
    () =>
      getTruncatedChartDates({
        aggregationInterval,
        startDate: minDate,
        endDate,
      }).map((date) => toISODateString(date)),
    [aggregationInterval, minDate, endDate],
  );

  const { chartData, oldestSnapshot } = useMemo(() => {
    const chartDataMap = new Map<
      string,
      {
        date: string;
        balanceValueUsd: number;
        balance: BigNumber;
        rewardsValueUsd: number;
        rewards: BigNumber;
      }
    >(
      chartDates.map((date) => [
        date,
        {
          date,
          balanceValueUsd: 0,
          balance: new BigNumber(0),
          rewardsValueUsd: 0,
          rewards: new BigNumber(0),
        },
      ]),
    );

    for (const snapshot of data?.volumeSnapshots?.groupedAggregates ?? []) {
      const truncatedDate = toISODateString(
        getTruncatedDataPointDate({
          date: new UTCDate(snapshot.date![0]),
          startDate,
          aggregationInterval,
        }),
      );

      const dataPoint = chartDataMap.get(truncatedDate);

      if (dataPoint) {
        chartDataMap.set(truncatedDate, {
          balanceValueUsd: Number(snapshot.max?.balanceValueUsd ?? 0),
          balance: new BigNumber(snapshot.max?.balance ?? 0),
          rewardsValueUsd: Number(snapshot.max?.rewardsValueUsd ?? 0),
          rewards: new BigNumber(snapshot.max?.rewards ?? 0),
          date: truncatedDate,
        });
      }
    }

    return {
      chartData: Array.from(chartDataMap.values()),
      oldestSnapshot: data?.oldestSnapshot?.nodes[0],
    };
  }, [data, chartDates, startDate, aggregationInterval]);

  const exportCsvData = useCallback(async () => {
    triggerCsvDownload(
      `${toISODateString(startDate)}-${toISODateString(endDate)}-${idSs58}-delegation-balance-data.csv`,
      [
        ['Date', 'Balance', 'Balance Value USD', 'Rewards', 'Rewards Value USD'],
        ...(data?.volumeSnapshots?.groupedAggregates ?? []).map((item) => [
          item.date ? toISODateString(new Date(item.date[0])) : '',
          TokenAmount.fromAsset(item.max?.balance ?? 0, 'Flip').toFormatted('numeral'),
          item.max?.balanceValueUsd ?? '',
          TokenAmount.fromAsset(item.max?.rewards ?? 0, 'Flip').toFormatted('numeral'),
          item.max?.rewardsValueUsd ?? '',
        ]),
      ],
    );
  }, [startDate, endDate, idSs58]);

  const canExportCsv = Boolean(oldestSnapshot);

  return {
    data: chartData,
    oldestSnapshot,
    exportCsvData: canExportCsv ? exportCsvData : undefined,
    isLoading,
  };
};
